import api from './client';

// Holiday type for scheduling
export interface Holiday {
  id?: number;
  name: string;
  date: string;
  recurring: boolean;
}

// Job type/category
export interface JobType {
  id?: number;
  name: string;
  color: string;
  defaultDuration?: number;
  defaultBudget?: number;
}

// Payment method configuration
export interface PaymentMethod {
  id: string;
  name: string;
  enabled: boolean;
  instructions?: string;
}

export interface Settings {
  general?: {
    // Units & Localization
    units?: 'imperial' | 'metric';
    currency?: string;
    currencySymbol?: string;
    dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
    timeFormat?: '12h' | '24h';
    timezone?: string;
    language?: string;
    // Working Hours
    workingHours?: {
      start: string;
      end: string;
    };
    businessDays?: string[];
    // Overtime
    overtime?: {
      enabled: boolean;
      dailyThreshold: number;
      weeklyThreshold: number;
      multiplier: number;
      doubleTimeEnabled?: boolean;
      doubleTimeMultiplier?: number;
    };
    unionWage?: boolean;
  };

  scheduling?: {
    // Calendar Preferences
    defaultCalendarView?: 'day' | 'week' | 'month' | 'year' | 'gantt';
    showWeekends?: boolean;
    weekStartsOn?: 'sunday' | 'monday';
    showWeekNumbers?: boolean;
    // Holidays
    holidays?: Holiday[];
    observeStatutoryHolidays?: boolean;
    // Capacity
    standardHoursPerWeek?: number;
    maxOvertimeHoursPerWeek?: number;
    // Scheduling Rules
    allowDoubleBooking?: boolean;
    requireCrewAssignment?: boolean;
    autoAssignProjectManager?: boolean;
    defaultScheduleDuration?: number;
    // Color Coding
    colorByStatus?: boolean;
    colorByCrew?: boolean;
  };

  jobs?: {
    // Job Numbering
    jobNumberPrefix?: string;
    jobNumberFormat?: 'sequential' | 'date-based' | 'year-sequential';
    nextJobNumber?: number;
    jobNumberSeparator?: string;
    // Job Types/Categories
    jobTypes?: JobType[];
    defaultJobType?: string;
    // Defaults
    defaultProjectManager?: number;
    defaultCrew?: number;
    defaultDuration?: number;
    // Budget & Alerts
    budgetAlertThreshold?: number;
    enableBudgetAlerts?: boolean;
    autoCreateBudgetFromEstimate?: boolean;
    // Requirements
    requireMaterialTracking?: boolean;
    requireDailyLogs?: boolean;
    requirePhotos?: boolean;
    // Status Workflow
    allowSkipStatuses?: boolean;
    requireApprovalToComplete?: boolean;
  };

  financial?: {
    // Currency & Formatting
    decimalPlaces?: number;
    thousandSeparator?: ',' | '.' | ' ';
    // Online card payments (Stripe) kill switch — absent = enabled
    stripePaymentsEnabled?: boolean;
    // Tax Settings
    salesTaxRate?: number;
    useTaxRate?: number;
    taxIncludedInPrice?: boolean;
    taxByLocation?: boolean;
    // Invoice Settings
    invoicePrefix?: string;
    nextInvoiceNumber?: number;
    invoiceNumberPadding?: number;
    resetInvoiceNumberAnnually?: boolean;
    defaultPaymentTerms?: number;
    lateFeePercentage?: number;
    lateFeeGracePeriod?: number;
    defaultInvoiceNotes?: string;
    invoiceTerms?: string;
    // Payment Methods
    paymentMethods?: PaymentMethod[];
    // Bank Info (for invoices)
    bankName?: string;
    bankAccountNumber?: string;
    bankRoutingNumber?: string;
    acceptCreditCards?: boolean;
    // Profit Margins
    laborMargin?: number;
    materialsMargin?: number;
    equipmentMargin?: number;
    subcontractorMargin?: number;
    overheadRate?: number;
    contingencyRate?: number;
    // Auto Actions
    autoSendInvoiceOnCompletion?: boolean;
    autoMarkOverdue?: boolean;
    sendPaymentReminders?: boolean;
    reminderDaysBefore?: number;
  };

  quotes?: {
    // Quote Numbering
    quotePrefix?: string;
    nextQuoteNumber?: number;
    quoteNumberPadding?: number;
    // Change Order Numbering
    changeOrderPrefix?: string;
    nextChangeOrderNumber?: number;
    // Quote Settings
    quoteExpirationDays?: number;
    autoExpireQuotes?: boolean;
    requireSignature?: boolean;
    requireDeposit?: boolean;
    defaultDepositPercent?: number;
    // Approval Workflow
    requireApproval?: boolean;
    approvalThreshold?: number;
    // Display Options
    showCostBreakdown?: boolean;
    showMarginPercent?: boolean;
    showUnitPrices?: boolean;
    // Default Sections
    defaultSections?: string[];
    // Terms & Conditions
    defaultQuoteTerms?: string;
    warrantyText?: string;
  };

  materials?: {
    // Labor Rates
    journeymanRate?: number;
    apprenticeRate?: number;
    foremanRate?: number;
    helperRate?: number;
    laborBurden?: number;
    minimumLaborCharge?: number;
    // Material Markups
    defaultMaterialMarkup?: number;
    conduitMarkup?: number;
    wireMarkup?: number;
    devicesMarkup?: number;
    fixturesMarkup?: number;
    panelsMarkup?: number;
    lumberMarkup?: number;
    hardwareMarkup?: number;
    miscMarkup?: number;
    // Waste Factors
    enableWasteFactor?: boolean;
    defaultWastePercent?: number;
    // Inventory Settings
    enableLowStockAlerts?: boolean;
    lowStockThreshold?: number;
    autoReorderEnabled?: boolean;
    defaultReorderQuantity?: number;
    // Assemblies
    useStandardAssemblies?: boolean;
  };

  notifications?: {
    // Email Notifications
    emailNotifications?: boolean;
    emailFrom?: string;
    emailReplyTo?: string;
    // Event Triggers
    notifyJobCreated?: boolean;
    notifyJobStatus?: boolean;
    notifyJobCompleted?: boolean;
    notifyQuoteSent?: boolean;
    notifyQuoteApproved?: boolean;
    notifyQuoteExpiring?: boolean;
    notifyInvoiceSent?: boolean;
    notifyPaymentReceived?: boolean;
    notifyOverdue?: boolean;
    notifyBudgetExceeded?: boolean;
    notifyLowStock?: boolean;
    notifyScheduleConflict?: boolean;
    notifyCrewAssignment?: boolean;
    // In-App Notifications
    inAppNotifications?: boolean;
    desktopNotifications?: boolean;
    soundEnabled?: boolean;
    // Notification Schedule
    quietHoursEnabled?: boolean;
    quietHoursStart?: string;
    quietHoursEnd?: string;
    // Reports
    dailySummaryEmail?: boolean;
    weeklySummaryEmail?: boolean;
    summaryEmailTime?: string;
    summaryEmailRecipients?: string[];
  };

  company?: {
    // Company Info
    companyName?: string;
    legalName?: string;
    licenseNumber?: string;
    taxId?: string;
    gstNumber?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    phone?: string;
    fax?: string;
    email?: string;
    website?: string;
    logoUrl?: string;
    // Branding
    primaryColor?: string;
    secondaryColor?: string;
    // Document Settings
    invoiceFooter?: string;
    quoteFooter?: string;
    emailSignature?: string;
    // Insurance & Bonding
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceCglLimit?: string;
    insuranceExpiration?: string;
    bondingCompany?: string;
    bondAmount?: number;
    // WCB / WorkSafeBC
    wcbAccount?: string;
    wcbClassification?: string;
    wcbAssessmentRate?: string;
    wcbEffectiveDate?: string;
    wcbClearanceLetterDate?: string;
  };

  agreements?: {
    clause_change_orders?:          string;
    clause_warranty?:               string;
    clause_lien_rights?:            string;
    clause_confidentiality?:        string;
    clause_termination?:            string;
    clause_dispute?:                string;
    clause_general_conditions?:     string;
    clause_schedule_time?:          string;
    clause_independent_contractor?: string;
    clause_wcb_ohs?:                string;
    clause_insurance_requirements?: string;
  };

  display?: {
    // Theme
    theme?: 'light' | 'dark' | 'system';
    compactMode?: boolean;
    // Pagination
    defaultPageSize?: number;
    // Dashboard
    dashboardWidgets?: string[];
    showQuickActions?: boolean;
    // Table Preferences
    defaultJobsSort?: string;
    defaultClientsSort?: string;
    showArchivedItems?: boolean;
  };
}

export const getSettings = async (): Promise<Settings> => {
  const response = await api.get('/settings');
  return response.data;
};

export const getSettingsByCategory = async (category: string): Promise<any> => {
  const response = await api.get(`/settings/${category}`);
  return response.data;
};

export const updateSettings = async (category: string, settings: any): Promise<Settings> => {
  const response = await api.put('/settings', { category, settings });
  return response.data;
};

export const updateMultipleCategories = async (categories: Record<string, any>): Promise<Settings> => {
  // Update each category
  const promises = Object.entries(categories).map(([category, settings]) =>
    api.put('/settings', { category, settings })
  );
  await Promise.all(promises);

  // Return fresh settings
  const response = await api.get('/settings');
  return response.data;
};
